import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { Mail, Calendar, Shield, ArrowLeft, EyeOff, User } from 'lucide-react';

const UserProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/users/${userId}/profile`);
        setProfile(data);
      } catch (err) {
        setError(err.response?.status === 403 ? 'Este perfil é privado.' : 'Utilizador não encontrado.');
      } finally {
        // Pequeno delay para suavizar a transição e evitar saltos
        setTimeout(() => setLoading(false), 200);
      }
    };
    fetchProfile();
  }, [userId]);

  // --- SKELETON PARA EVITAR LAYOUT SHIFT ---
  if (loading) return (
    <div className="max-w-4xl mx-auto space-y-8 py-10 animate-pulse">
      <div className="h-4 w-20 bg-slate-200 dark:bg-slate-800 rounded-lg"></div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="h-[360px] bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700"></div>
        <div className="md:col-span-2 h-[360px] bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700"></div>
      </div>
    </div>
  );

  if (error) return (
    <div className="max-w-md mx-auto mt-20 p-8 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl text-center shadow-sm transition-colors">
        <div className="w-14 h-14 bg-red-50 dark:bg-red-900/20 text-red-500 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <EyeOff size={28} />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2 tracking-tight">Perfil indisponível</h3>
        <p className="text-slate-500 dark:text-slate-400 text-sm mb-6 leading-relaxed">{error}</p>
        <button onClick={() => navigate(-1)} className="text-sm font-medium text-blue-600 hover:underline">Voltar</button>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto space-y-8 py-10 animate-in fade-in duration-300">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-400 hover:text-blue-600 transition-colors text-sm font-medium">
        <ArrowLeft size={16} /> <span>Voltar</span>
      </button>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
        {/* Painel de avatar */}
        <div className="flex flex-col items-center justify-center p-8 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl shadow-sm transition-colors">
          {profile?.profile_pic_url ? (
            <img src={profile.profile_pic_url} className="w-28 h-28 rounded-2xl object-cover border border-slate-200 dark:border-slate-700" alt="Perfil" />
          ) : (
            <div className="w-28 h-28 bg-blue-600 rounded-2xl flex items-center justify-center text-white text-3xl font-semibold uppercase">
              {profile?.email[0]}
            </div>
          )}
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-5 tracking-tight text-center line-clamp-2">
            {profile?.full_name || 'Sem nome'}
          </h3>
          <span className="text-xs bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 px-3 py-1 rounded-full font-medium mt-3">
            {profile?.global_role}
          </span>
        </div>

        {/* Painel de info */}
        <div className="md:col-span-2 bg-white dark:bg-slate-800 p-8 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm transition-colors flex flex-col justify-center">
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-xl text-slate-400 border border-slate-100 dark:border-slate-700"><Mail size={20}/></div>
                    <div className="min-w-0">
                        <p className="text-xs text-slate-400 mb-0.5">Email</p>
                        <p className="font-medium text-slate-800 dark:text-slate-100 truncate">{profile?.email}</p>
                    </div>
                </div>

                {profile?.birth_date && (
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-xl text-slate-400 border border-slate-100 dark:border-slate-700"><Calendar size={20}/></div>
                        <div>
                            <p className="text-xs text-slate-400 mb-0.5">Nascimento</p>
                            <p className="font-medium text-slate-800 dark:text-slate-100">{new Date(profile.birth_date).toLocaleDateString('pt-PT', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                        </div>
                    </div>
                )}

                <div className="flex items-center gap-4">
                    <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-xl text-slate-400 border border-slate-100 dark:border-slate-700"><Shield size={20}/></div>
                    <div>
                        <p className="text-xs text-slate-400 mb-0.5">Papel</p>
                        <p className="font-medium text-slate-800 dark:text-slate-100">{profile?.global_role}</p>
                    </div>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
